import * as React from 'react';
import { 
  MessageSquare, 
  Users, 
  TrendingUp,
  CheckCircle,
  Clock
} from 'lucide-react';
import { Badge, Card, CardBody, CardHeader, EmptyState, ErrorState, LoadingState, Page, PageHeader, PageSection, Select } from '@/components';
import { useSearchParams } from 'react-router-dom';
import { useDashboardMetrics } from '@/hooks';
import { useConversations } from '@/hooks';
import type { DashboardRange } from '@/services/dashboardService';
import type { ChannelType } from '@/services/channelsService';

export const Dashboard: React.FC = () => {
  const [params, setParams] = useSearchParams();

  const range = (params.get('range') ?? '7d') as DashboardRange;
  const channel = (params.get('channel') ?? 'all') as ChannelType | 'all';

  const metricsQuery = useDashboardMetrics({ range, channel });
  const conversationsQuery = useConversations({ limit: 5 });

  return (
    <Page className="page--dashboard">
      <PageHeader
        title="Dashboard"
        description="Visão geral dos WhatsApps e integrações de todos os projetos"
        right={
          <div className="dashboard-filters">
            <Select
              value={range}
              onChange={(e) =>
                setParams((prev) => {
                  prev.set('range', e.target.value);
                  return prev;
                })
              }
              aria-label="Período"
            >
              <option value="today">Hoje</option>
              <option value="7d">Últimos 7 dias</option>
              <option value="30d">Últimos 30 dias</option>
            </Select>
            <Select
              value={channel}
              onChange={(e) =>
                setParams((prev) => {
                  prev.set('channel', e.target.value);
                  return prev;
                })
              }
              aria-label="Canal"
            >
              <option value="all">Todos</option>
              <option value="whatsapp">WhatsApp</option>
              <option value="webchat">Web Chat</option>
            </Select>
          </div>
        }
      />

      <PageSection>
        {metricsQuery.isLoading ? (
          <LoadingState title="Carregando métricas" description="Buscando indicadores do dashboard." />
        ) : metricsQuery.isError ? (
          <ErrorState title="Falha ao carregar métricas" onRetry={() => metricsQuery.refetch()} />
        ) : !metricsQuery.data ? (
          <EmptyState title="Sem métricas" description="Nenhum dado disponível para os filtros atuais." />
        ) : (
        <div className="stats-grid">
          <Card>
            <CardBody>
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">Mensagens enviadas</p>
                  <p className="text-2xl font-bold text-gray-900">{metricsQuery.data.sent.toLocaleString('pt-BR')}</p>
                </div>
                <div className="w-12 h-12 bg-gray-100 rounded flex items-center justify-center">
                  <MessageSquare className="w-6 h-6 text-gray-600" />
                </div>
              </div>
              <div className="mt-4 flex items-center">
                <TrendingUp className="w-4 h-4 text-green-500 mr-1" />
                <span className="text-sm text-gray-600">Filtrado por período e canal</span>
              </div>
            </CardBody>
          </Card>

          <Card>
            <CardBody>
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">Taxa de Sucesso</p>
                  <p className="text-2xl font-bold text-gray-900">{metricsQuery.data.success_rate}%</p>
                </div>
                <div className="w-12 h-12 bg-gray-100 rounded flex items-center justify-center">
                  <CheckCircle className="w-6 h-6 text-gray-600" />
                </div>
              </div>
            </CardBody>
          </Card>

          <Card>
            <CardBody>
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">Contatos</p>
                  <p className="text-2xl font-bold text-gray-900">{metricsQuery.data.contacts.toLocaleString('pt-BR')}</p>
                </div>
                <div className="w-12 h-12 bg-gray-100 rounded flex items-center justify-center">
                  <Users className="w-6 h-6 text-gray-600" />
                </div>
              </div>
            </CardBody>
          </Card>
        </div>
        )}
      </PageSection>

      <PageSection>
        <div className="content-grid">
          <Card>
            <CardHeader>
              <h3 className="text-lg font-semibold text-gray-900">Conversas Recentes</h3>
            </CardHeader>
            <CardBody className="p-0">
            <div className="overflow-x-auto">
              {conversationsQuery.isLoading ? (
                <LoadingState title="Carregando conversas" description="Buscando conversas recentes." />
              ) : conversationsQuery.isError ? (
                <ErrorState title="Falha ao carregar conversas" onRetry={() => conversationsQuery.refetch()} />
              ) : conversationsQuery.data?.data?.length ? (
                <table className="table">
                  <thead>
                    <tr>
                      <th>Canal</th>
                      <th>Contato</th>
                      <th>Status</th>
                      <th>Última Atividade</th>
                    </tr>
                  </thead>
                  <tbody>
                    {conversationsQuery.data.data.slice(0, 5).map((item: any) => (
                      <tr key={item.id}>
                        <td className="font-medium">{item.channel}</td>
                        <td className="font-medium">{item.contacts?.name || item.contacts?.phone || item.contact_id}</td>
                        <td>
                          <Badge variant={item.status === 'open' ? 'success' : 'neutral'}>
                            {item.status}
                          </Badge>
                        </td>
                        <td className="text-gray-500">
                          {item.last_message_at 
                            ? new Date(item.last_message_at).toLocaleString('pt-BR') 
                            : item.updated_at 
                              ? new Date(item.updated_at).toLocaleString('pt-BR')
                              : '-'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : (
                <EmptyState 
                  title="Nenhuma conversa recente" 
                  description="As conversas aparecerão aqui quando houver mensagens."
                />
              )}
            </div>
            </CardBody>
          </Card>

          <Card>
            <CardHeader>
              <h3 className="text-lg font-semibold text-gray-900">Status do Sistema</h3>
            </CardHeader>
            <CardBody className="space-y-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <CheckCircle className="w-5 h-5 text-green-500" />
                <span className="text-sm font-medium">WhatsApp API</span>
              </div>
              <span className="badge badge-success">Online</span>
            </div>
            
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <CheckCircle className="w-5 h-5 text-green-500" />
                <span className="text-sm font-medium">Database</span>
              </div>
              <span className="badge badge-success">Online</span>
            </div>
            
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Clock className="w-5 h-5 text-yellow-500" />
                <span className="text-sm font-medium">Scheduler</span>
              </div>
              <span className="badge badge-warning">Processando</span>
            </div>
            
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <CheckCircle className="w-5 h-5 text-green-500" />
                <span className="text-sm font-medium">Dex API</span>
              </div>
              <span className="badge badge-success">Online</span>
            </div>

            <div className="pt-4 border-t border-gray-200">
              <div className="flex items-center justify-between text-sm">
                <span className="text-gray-600">Última verificação</span>
                <span className="text-gray-900">{new Date().toLocaleString('pt-BR')}</span>
              </div>
            </div>
            </CardBody>
          </Card>
        </div>
      </PageSection>

      <PageSection>
        <Card>
          <CardHeader>
            <h3 className="text-lg font-semibold text-gray-900">Ações Rápidas</h3>
          </CardHeader>
          <CardBody>
            <div className="dashboard__quick-actions">
            <button className="btn btn-primary">
              <MessageSquare className="w-4 h-4" />
              Abrir Conversa
            </button>
            <button className="btn btn-secondary">
              <Users className="w-4 h-4" />
              Gerenciar Contatos
            </button>
            </div>
          </CardBody>
        </Card>
      </PageSection>
    </Page>
  );
};
